 <template>
    <div id="song" ref="Song" v-resize="onResize">
        <div class="song-container">
            <!-- Actions -->
            <div id="song-buttons" class="text-xs-right" v-if="!isMobile">
                <v-btn class="song_edit" icon light flat @click.native="toggleEditionModeAction()">
                    <v-icon>{{ editSongIcon }}</v-icon>
                </v-btn>
                <v-btn class="song_print" icon light flat @click="printSong()">
                    <v-icon>print</v-icon>
                </v-btn>
            </div>

            <!-- Titre / Auteur / Album -->
            <div class="title">
                <template v-if="editionMode && song.title === ''">{{ $t('title') }}</template>
                <template v-else>{{ song.title }}</template>
            </div>
            <div class="author">
                <template v-if="editionMode && song.author === ''">{{ $t('author') }}</template>
                <template v-else>{{ song.author }}</template>
            </div>
            <div class="album" v-if="song.album !== ''">
                {{ song.album }}
            </div>

            <!-- Informations chanson -->
            <v-layout align-start justify-center row fill-height>
                <v-flex xs6>
                    <div class="key" v-if="song.key !== ''">
                        {{ $t('key') }} : <b>{{ song.key }}</b>
                    </div>
                    <div class="key" v-if="song.timeSignature">
                        {{ $t('timeSignature') }} : <b>{{ song.timeSignature }}</b>
                    </div>
                    <div class="tempo" v-if="song.tempo !== 0 && song.tempo !== ''">
                        <span v-html="'&#x2669;'"></span>&nbsp;:&nbsp;{{ song.tempo }}
                    </div>
                </v-flex>
                <v-flex xs6>
                    <div class="capo" v-if="song.capo !== 0 && song.capo !== ''">
                        {{ $t('capo') }} : <b>{{ song.capo }}</b>
                    </div>
                </v-flex>
            </v-layout>

            <div class="grid mt-2">
                <div v-for="(part, index) in song.parts" :key="index" :style="{ display: 'block', width: (width * columns) + 'px' }">
                    <!-- Partie -->
                    <template v-if="!editionMode">
                        <div class="part-title" v-if="part.name !== '' && part.name !== null">
                            {{ part.name }}
                        </div>
                    </template>
                    <template v-else>
                        <v-layout align-center justify-space-between row>
                            <v-text-field v-model="partsName[index]" v-on:blur="updatePartName(index, partsName[index])"
                                          :label="$t('part-name')" class="pa-2 mt-4" light clearable></v-text-field>
                            <v-spacer></v-spacer>
                            <v-btn icon flat color="error" @click.stop="deletePart(index)">
                                <v-icon small>delete</v-icon>
                            </v-btn>
                        </v-layout>
                    </template>

                    <!-- Mesure -->
                    <div class="measures-container">
                        <div v-for="(measure1, index2) in measuresForGrid[index]" :key="index2" class="measure-container-line">
                            <measure v-for="(measure, index3) in measure1" :key="index3" :isEditable="editionMode"
                                     :partIndex="index" :measureIndex="index2 * columns + index3" :width="width" :height="height" :measure="measure" ref="Measures"
                                     @click.native="editMeasure(index, index2 * columns + index3)"/>
                            <div class="add-chord" :style="{ height: height + 'px', width: width + 'px' }"
                                 v-if="editionMode && index2 === measuresForGrid[index].length - 1 && !placeAddMeasureIntoPartOnNewLine[index]">
                                <v-btn color="success" icon large dark flat @click="addMeasure(index)">
                                    <v-icon large>add</v-icon>
                                </v-btn>
                            </div>
                        </div>
                        <div class="add-chord" v-if="editionMode && placeAddMeasureIntoPartOnNewLine[index]"
                             :style="{ height: height + 'px', width: width + 'px' }">
                            <v-btn color="success" icon large dark flat @click="addMeasure(index)">
                                <v-icon large>add</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Commentaire -->
            <div class="comment">
                <template v-if="!editionMode">
                    <template v-if="song.comment.length > 0">
                        <div class="comment-title">{{ $t('comment') }}</div>
                        <div class="comment-text" v-html="song.comment"></div>
                    </template>
                </template>
                <template v-else>
                    <v-textarea v-model="comment" rows="2" :label="$t('comment')" light auto-grow
                                v-on:blur="updateSongAction({ property: 'comment', value: comment })"></v-textarea>
                </template>
            </div>
        </div>
        
        <measure-editor-dialog ref="MeasureEditorDialog2"></measure-editor-dialog>
        <delete-part-dialog ref="DeletePartDialog"></delete-part-dialog>
    </div>
</template>

<script>
// @ is an alias to /src
import { mapGetters, mapActions } from 'vuex';
import Measure from '@/components/Measure.vue';
import MeasureEditorDialog from '@/components/dialogs/MeasureEditorDialog.vue';
import DeletePartDialog from '@/components/dialogs/DeletePartDialog.vue';
import store from '@/stores';
import * as types from '@/stores/types';

export default {
    name:  'song',
    store: store,
    data:  function()
    {
        return {
            columns:  4,
            width:    170,
            height:   80,
            items:    [
                { icon: 'home', text: 'Home', route: '/' },
                { icon: 'info', text: 'About', route: '/about' },
            ],
        };
    },
    components: {
        Measure,
        MeasureEditorDialog,
        DeletePartDialog,
    },
    mounted: function()
    {
        this.onResize();
    },
    updated: function()
    {
        this.onResize();
    },
    computed: {
        ...mapGetters({
            song:         'song',
            editionMode:  'editionMode',
            selectedPart: 'selectedPart',
            isMobile:     'isMobile',
        }),
        partsName: function()
        {
            return this.song.parts.map(element => element.name);
        },
        comment: {
            get: function()
            {
                return this.song.comment;
            },
            set: function(value)
            {
                this.$store.commit(types.M_SONG_UPDATE, { property: 'comment', value });
            },
        },
        editSongColor: function()
        {
            return (this.editionMode) ? 'primary' : 'primary';
        },
        editSongIcon: function()
        {
            return (this.editionMode) ? 'visibility' : 'edit';
        },
        measuresForGrid: function()
        {
            const data           = [...this.song.parts];
            let measuresSplitted = [];
            let measures, j, k;

            for (const i in data)
            {
                measuresSplitted[i] = [];
                measures = [...data[i].measures];
                k = data[i].measures.length;

                j = 0;
                while (k >= this.columns)
                {
                    measuresSplitted[i].push(measures.slice(j, j + this.columns));

                    j += this.columns;
                    k -= this.columns;
                }

                if (k !== 0)
                {
                    measuresSplitted[i].push(measures.slice(j));
                }
            }

            return measuresSplitted;
        },
        // Placement du bouton ajouter à la ligne ou non
        placeAddMeasureIntoPartOnNewLine: function()
        {
            return this.song.parts.map(part => part.measures.length % this.columns === 0);
        },
    },
    methods: {
        ...mapActions({
            savePartNameAction:      types.A_SONG_SAVE_PART_NAME,
            updateSongAction:        types.A_SONG_UPDATE,
            toggleEditionModeAction: types.A_SONG_TOGGLE_EDITION_MODE,
            setEditionModeAction:    types.A_SONG_SET_EDITION_MODE,
        }),
        /**
         * Redimensionnement du composant
         *
         * @author Fabien Bellanger
         */
        onResize: function()
        {
            if (this.isMobile)
            {
                this.columns = (window.innerWidth < 600) ? 2 : 4;
                this.width = this.$refs.Song.clientWidth / this.columns - 16;
                this.height = (window.innerWidth < 600) ? 70 : 80;

                this.setEditionModeAction(false);
            }
            else
            {
                if (this.editionMode && window.innerWidth > 960)
                {
                    this.width = 170;
                }
                else
                {
                    this.width = this.$refs.Song.clientWidth / this.columns - 16;
                }
                
                this.height = 80;
                this.columns = 4;
            }
            
            // On redessine toutes les mesures
            this.redrawAllMeasures();
        },
        /**
         * On redessine toutes les mesures
         *
         * @author Fabien Bellanger
         */
        redrawAllMeasures: function()
        {
            if (this.$refs.Measures !== undefined)
            {
                this.$refs.Measures.forEach((measure) =>
                {
                    measure.redraw();
                });
            }
        },
        /**
         * Ajout d'une mesure
         *
         * @author Fabien Bellanger
         */
        addMeasure: function(index)
        {
            this.$refs.MeasureEditorDialog2.open(index, -1, true);
        },
        /**
         * Edition d'une mesure
         *
         * @author Fabien Bellanger
         */
        editMeasure: function(indexPart, indexMeasure)
        {
            if (this.editionMode)
            {
                this.$refs.MeasureEditorDialog2.open(indexPart, indexMeasure, false);
            }
        },
        /**
         * Mise à jour du nom de la partie
         *
         * @author Fabien Bellanger
         * @param {Number}  index   Indice
         * @param {String}  name    Nom de la partie
         */
        updatePartName: function(index, name)
        {
            this.savePartNameAction({ index: index, name: name });
        },
        /**
         * Suppression d'une mesure
         *
         * @author Fabien Bellanger
         */
        deletePart: function(index)
        {
            this.$refs.DeletePartDialog.open(index);
        },
        /**
         * Impression d'une chanson
         *
         * @author Fabien Bellanger
         */
        printSong: function()
        {
            const printSongRoute = this.$router.resolve({ name: 'print-song' }).href;

            if (printSongRoute !== '#/')
            {
                window.open(window.location.origin + '/' + printSongRoute);
            }
        },
    },
};
</script>

<style scoped lang="scss">
    #song {
        height:           100%;
        max-width:        720px;
        min-width:        330px;
        padding:          10px;
        padding-bottom:   50px;
        overflow:         auto;
        background-color: #f6f6f6;
        color:            #111;
        margin:           0px auto 40px auto;

        .song-container {
            padding: 0px;
        }

        .title {
            margin:    16px 0px 8px 0px;
            font-size: 30px !important;
        }

        .author {
            margin:     4px 0px 0px 0px;
            font-size:  20px;
            font-style: italic;
        }

        .album {
            margin:      0px 0px 6px 0px;
            font-size:   18px;
            font-weight: 400;
            font-style:  italic;
            color:       #444;
        }

        .grid {
            display:     flex;
            flex-flow:   column nowrap;
            align-items: center;

            .part-title {
                margin:         20px 0px 10px 0px;
                text-align:     left;
                font-size:      18px;
                font-weight:    500;
                text-transform: capitalize;
            }

            .add-chord {
                display:         flex;
                flex-flow:       row nowrap;
                justify-content: center;
                align-items:     center;
                margin:          0.5px 0px 0px -0.5px;
            }

            .measures-container {
                display:   flex;
                flex-flow: column nowrap;

                .measure-container-line {
                    display:     flex;
                    flex-flow:   row nowrap;
                    align-items: flex-end;

                    .add-chord {
                        margin: -1px 0px 0px 0px;
                    }
                }
            }
        }

        .key, .tempo {
            margin:      0px 0px 0px 20px;
            font-size:   16px;
            text-align:  left;
        }

        .capo {
            margin:      0px 24px 0px 0px;
            font-size:   16px;
            text-align:  right;
        }

        .tempo > span {
            font-size: 20px;
        }

        .comment {
            margin:     40px 0px 20px 10px;
            text-align: left;

            &-title {
                margin-bottom: 10px;
                font-size:     18px;
                font-weight:   500;
            }

            &-text {
                white-space: pre;
            }
        }
    }
</style>
